// Единственная точка похода в сеть (docs/09-code-standards.md §8): компоненты не вызывают fetch.
// Базовый путь относительный — витрина и API живут на одном origin за nginx.
import type { ApiErrorCode, ApiErrorResponse } from "@shopkit/shared";

import { messages } from "../lib/messages";
import { getToken } from "./tokenStore";

// В браузере отмена запроса работает штатно, а в jsdom (тесты) fetch приходит из Node
// и не принимает AbortSignal, созданный jsdom. Проверяем возможность один раз при загрузке:
// без отмены запрос просто доживает до конца, его результат TanStack Query всё равно отбрасывает.
const FETCH_ACCEPTS_SIGNAL = detectSignalSupport();

function detectSignalSupport(): boolean {
  try {
    new Request(window.location.origin, { signal: new AbortController().signal });
    return true;
  } catch {
    return false;
  }
}

/**
 * ApiError — ошибка API в разобранном виде: HTTP-статус и машиночитаемый код
 * (docs/04-api-contract.md §1). status = 0 означает, что ответа не было вовсе (обрыв связи).
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ApiErrorCode,
    readonly fields: Readonly<Record<string, string>> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiRequestOptions extends RequestInit {
  /** Запрос не несёт Bearer-токен и не должен запускать переавторизацию на 401 (auth, healthz). */
  skipAuth?: boolean;
}

/** reauthenticate — обменивает Telegram initData на новый JWT (docs/07-miniapp-ux.md §3). */
type Reauthenticate = () => Promise<void>;
let reauthenticate: Reauthenticate | null = null;

/**
 * configureReauth задаёт способ прозрачно получить новый токен при просроченном JWT.
 * Вызывается один раз бутстрапом витрины, когда известны slug и initData.
 */
export function configureReauth(fn: Reauthenticate | null): void {
  reauthenticate = fn;
}

/**
 * apiRequest выполняет запрос к API и возвращает разобранное тело ответа.
 * Бросает ApiError при любом ответе не 2xx и при обрыве связи; отмену запроса (AbortError)
 * пропускает как есть — её обрабатывает TanStack Query.
 * Просроченный токен (401) фронт молча обменивает на новый через configureReauth
 * и повторяет исходный запрос один раз (docs/07-miniapp-ux.md §3).
 */
export async function apiRequest<T>(path: string, init: ApiRequestOptions = {}, retried = false): Promise<T> {
  const { signal, skipAuth, ...rest } = init;
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (!skipAuth) {
    const token = getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  let response: Response;
  try {
    response = await fetch(absoluteUrl(path), {
      ...rest,
      ...(FETCH_ACCEPTS_SIGNAL && signal ? { signal } : {}),
      headers,
      credentials: "same-origin",
    });
  } catch (cause) {
    // Отменённый запрос (уход со страницы, новый запрос вместо старого) — не сбой сети:
    // его обрабатывает TanStack Query, и показывать пользователю нечего.
    if (signal?.aborted || (cause instanceof Error && cause.name === "AbortError")) {
      throw cause;
    }
    throw new ApiError(messages.network, 0, "internal");
  }

  if (response.status === 401 && !skipAuth && !retried && reauthenticate) {
    await reauthenticate();
    return apiRequest<T>(path, init, true);
  }

  if (!response.ok) {
    throw await toApiError(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * absoluteUrl приводит путь к абсолютному виду.
 * fetch в jsdom (тесты) не умеет относительные пути, а в браузере результат тот же.
 */
function absoluteUrl(path: string): string {
  return new URL(path, window.location.origin).toString();
}

/** toApiError разбирает тело ошибки; если тела нет — оставляет текст по статусу. */
async function toApiError(response: Response): Promise<ApiError> {
  const fallback = statusMessage(response.status);
  const fallbackCode = statusCode(response.status);

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return new ApiError(fallback, response.status, fallbackCode);
  }

  const error = (body as Partial<ApiErrorResponse>).error;
  if (!error) {
    return new ApiError(fallback, response.status, fallbackCode);
  }
  return new ApiError(error.message || fallback, response.status, error.code ?? fallbackCode, error.fields ?? {});
}

function statusMessage(status: number): string {
  if (status >= 500) return messages.requestFailed;
  switch (status) {
    case 401:
      return messages.requestFailed;
    case 404:
      return messages.invalidLink;
    default:
      return messages.requestFailed;
  }
}

function statusCode(status: number): ApiErrorCode {
  if (status >= 500) return "internal";
  switch (status) {
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "not_found";
    case 409:
      return "shop_unavailable";
    case 429:
      return "rate_limited";
    default:
      return "internal";
  }
}
